import { useEffect, useState } from 'react';
import { api } from '../api/client';
import type { JournalEntry } from '../api/types';

const MAX_TEXT_LENGTH = 20_000;

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function Journal() {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .listJournalEntries()
      .then((res) => setEntries(res.entries))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load your journal.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const entry = await api.createJournalEntry({ text: draft });
      setEntries((prev) => [entry, ...prev]);
      setDraft('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save this entry.');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (entry: JournalEntry) => {
    setEditingId(entry.entryId);
    setEditingText(entry.text);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingText('');
  };

  const handleSaveEdit = async (entryId: string) => {
    if (!editingText.trim()) return;
    setError(null);
    try {
      const updated = await api.updateJournalEntry(entryId, { text: editingText });
      setEntries((prev) => prev.map((e) => (e.entryId === entryId ? updated : e)));
      cancelEditing();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save your changes.');
    }
  };

  const handleDelete = async (entryId: string) => {
    setError(null);
    try {
      await api.deleteJournalEntry(entryId);
      setEntries((prev) => prev.filter((e) => e.entryId !== entryId));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete this entry.');
    }
  };

  const handleExport = async () => {
    setError(null);
    try {
      const res = await api.exportJournal();
      const blob = new Blob([JSON.stringify(res.entries, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'omnist-journal-export.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not export your journal.');
    }
  };

  return (
    <div>
      <h1 className="page-title">Journal</h1>
      <p className="page-subtitle">
        A private space for reflection -- only you can read these entries.
      </p>

      {error && <p className="error-text">{error}</p>}

      <div className="themed-card" style={{ marginBottom: 20 }}>
        <textarea
          className="journal-textarea"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="What's on your mind today?"
          maxLength={MAX_TEXT_LENGTH}
          rows={4}
        />
        <button
          type="button"
          className="btn btn-primary btn-block"
          onClick={handleCreate}
          disabled={saving || !draft.trim()}
          style={{ marginTop: 10 }}
        >
          {saving ? 'Saving...' : 'Add entry'}
        </button>
      </div>

      {entries.length > 0 && (
        <button type="button" className="btn btn-ghost" onClick={handleExport} style={{ marginBottom: 16 }}>
          Export all entries
        </button>
      )}

      {loading ? (
        <div className="center-page" style={{ minHeight: '30vh' }}>
          <div className="spinner" />
        </div>
      ) : entries.length === 0 ? (
        <div className="empty-state">
          No journal entries yet. Whatever you write here stays private to you.
        </div>
      ) : (
        <div className="journal-list">
          {entries.map((entry) => (
            <div key={entry.entryId} className="journal-entry-card">
              <div className="journal-entry-meta">{formatTimestamp(entry.createdAt)}</div>
              {editingId === entry.entryId ? (
                <>
                  <textarea
                    className="journal-textarea"
                    value={editingText}
                    onChange={(e) => setEditingText(e.target.value)}
                    maxLength={MAX_TEXT_LENGTH}
                    rows={4}
                  />
                  <div className="journal-entry-actions">
                    <button type="button" className="btn btn-primary" onClick={() => handleSaveEdit(entry.entryId)}>
                      Save
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={cancelEditing}>
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="journal-entry-text">{entry.text}</p>
                  <div className="journal-entry-actions">
                    <button type="button" className="btn btn-ghost" onClick={() => startEditing(entry)}>
                      Edit
                    </button>
                    <button type="button" className="btn btn-ghost" onClick={() => handleDelete(entry.entryId)}>
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
